import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AUTH_SCHEME,
  fetchUserStatus,
  loginWithCredentials,
  loginWithGoogleCredential,
  performLogout,
} from "../features/auth/authClient";
import {
  clearAuthSnapshot,
  persistAuthSnapshot,
  readAuthSnapshot,
} from "../features/auth/authStorage";
import { createAuthorizedFetcher } from "../features/auth/createAuthorizedFetcher";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

const DEFAULT_SESSION_STATE = {
  token: null,
  username: null,
  isEmailVerified: false,
  hasAllegroConnected: false,
};

const createInitialSession = () => ({
  ...DEFAULT_SESSION_STATE,
  ...readAuthSnapshot(),
});

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(createInitialSession);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [authError, setAuthError] = useState(null);

  const tokenRef = useRef(session.token);
  useEffect(() => {
    tokenRef.current = session.token;
  }, [session.token]);

  const updateSession = useCallback((updater) => {
    setSession((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      persistAuthSnapshot({
        token: next.token,
        username: next.username,
        isEmailVerified: next.isEmailVerified,
        hasAllegroConnected: next.hasAllegroConnected,
      });
      return next;
    });
  }, []);

  const clearSession = useCallback(() => {
    clearAuthSnapshot();
    setSession(DEFAULT_SESSION_STATE);
  }, []);

  const performLogoutFlow = useCallback(
    async ({ suppressLoading } = {}) => {
      if (!suppressLoading) {
        setLoadingAuth(true);
        setAuthError(null);
      }
      try {
        if (tokenRef.current) {
          await performLogout(tokenRef.current);
        }
      } finally {
        clearSession();
        if (!suppressLoading) {
          setLoadingAuth(false);
        }
      }
    },
    [clearSession]
  );

  const logout = useCallback(
    () => performLogoutFlow({ suppressLoading: false }),
    [performLogoutFlow]
  );

  const silentLogout = useCallback(
    () => performLogoutFlow({ suppressLoading: true }),
    [performLogoutFlow]
  );

  const runLoginFlow = useCallback(
    async ({ token, username }, { unverifiedMessage }) => {
      const status = await fetchUserStatus(token);

      if (!status?.is_email_verified) {
        clearSession();
        setAuthError(
          unverifiedMessage ||
            "Please verify your email address before signing in. Use the link we sent to your inbox."
        );
        return false;
      }

      updateSession({
        token,
        username,
        isEmailVerified: Boolean(status.is_email_verified),
        hasAllegroConnected: Boolean(status.has_allegro_connected),
      });

      return true;
    },
    [clearSession, updateSession]
  );

  const login = useCallback(
    async (email, password) => {
      setLoadingAuth(true);
      setAuthError(null);
      try {
        const credentials = await loginWithCredentials({ email, password });
        return await runLoginFlow(credentials, {
          unverifiedMessage:
            "Please verify your email address before signing in. Use the link we sent to your inbox.",
        });
      } catch (error) {
        console.error("Login failed:", error);
        setAuthError(error.message || "Login failed. Please try again.");
        clearSession();
        return false;
      } finally {
        setLoadingAuth(false);
      }
    },
    [runLoginFlow, clearSession]
  );

  const loginWithGoogle = useCallback(
    async (credential) => {
      setLoadingAuth(true);
      setAuthError(null);
      try {
        const credentials = await loginWithGoogleCredential(credential);
        return await runLoginFlow(credentials, {
          unverifiedMessage:
            "Please verify your email address before signing in with Google. Use the link we sent to your inbox.",
        });
      } catch (error) {
        console.error("Google login failed:", error);
        setAuthError(error.message || "Google login failed. Please try again.");
        clearSession();
        return false;
      } finally {
        setLoadingAuth(false);
      }
    },
    [runLoginFlow, clearSession]
  );

  useEffect(() => {
    let cancelled = false;
    if (!session.token) return undefined;

    (async () => {
      try {
        const status = await fetchUserStatus(session.token);
        if (cancelled) return;
        updateSession((prev) => ({
          ...prev,
          isEmailVerified: Boolean(status.is_email_verified),
          hasAllegroConnected: Boolean(status.has_allegro_connected),
        }));
      } catch (error) {
        if (cancelled) return;
        if (error.status === 401 || error.status === 403) {
          await silentLogout();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session.token, updateSession, silentLogout]);

  const setIsEmailVerifiedValue = useCallback(
    (value) => updateSession({ isEmailVerified: Boolean(value) }),
    [updateSession]
  );

  const setHasAllegroConnectedValue = useCallback(
    (value) => updateSession({ hasAllegroConnected: Boolean(value) }),
    [updateSession]
  );

  const authFetch = useMemo(() => {
    if (!session.token) {
      return async () => {
        throw new Error("No auth token");
      };
    }
    return createAuthorizedFetcher(session.token, silentLogout);
  }, [session.token, silentLogout]);

  const isAuthenticated = Boolean(session.token);

  const value = {
    authToken: session.token,
    isAuthenticated,
    username: session.username,
    isEmailVerified: session.isEmailVerified,
    hasAllegroConnected: session.hasAllegroConnected,
    loadingAuth,
    authError,
    login,
    loginWithGoogle,
    logout,
    setAuthError,
    setIsEmailVerified: setIsEmailVerifiedValue,
    setHasAllegroConnected: setHasAllegroConnectedValue,
    authFetch,
    AUTH_SCHEME,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
